"use client";
import * as React from "react";
import { SanityImage } from "./image-types";
import { getSanityImageURL } from "../../helpers/sanity-image-url-builder";
import { useEffect, useRef, useState } from "react";

const PLACEHOLDER_IMAGE_SMALL =
  "data:image/svg+xml;charset=utf-8;base64,PHN2ZyB2aWV3Qm94PSIwIDAgNjY0IDY2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCiAgPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzFfNCkiPg0KICAgIDxwYXRoIGQ9Ik02NjQgMEgwVjY2NEg2NjRWMFoiIGZpbGw9IiNGMkYyRjIiLz4NCiAgICA8cGF0aCBkPSJNMjY5LjkgMzQxTDMwNSA0MTFIMjM1TDI2OS45IDM0MVoiIGZpbGw9IiM0MTQxNDEiLz4NCiAgICA8cGF0aCBkPSJNMzQ4LjQgMjU0TDQyNyA0MTFIMjcwTDM0OC40IDI1NFoiIGZpbGw9IiM3RjdGN0YiLz4NCiAgICA8cGF0aCBkPSJNMjY5LjggMjk2LjkxQzI3OS40MSAyOTYuOTEgMjg3LjIgMjg5LjEgMjg3LjIgMjc5LjQ2NkMyODcuMiAyNjkuODMyIDI3OS40MSAyNjIuMDIyIDI2OS44IDI2Mi4wMjJDMjYwLjE5IDI2Mi4wMjIgMjUyLjQgMjY5LjgzMiAyNTIuNCAyNzkuNDY2QzI1Mi40IDI4OS4xIDI2MC4xOSAyOTYuOTEgMjY5LjggMjk2LjkxWiIgZmlsbD0iI0VDMUQyNCIvPg0KICA8L2c+DQogIDxkZWZzPg0KICAgIDxjbGlwUGF0aCBpZD0iY2xpcDBfMV80Ij4NCiAgICAgIDxyZWN0IHdpZHRoPSI2NjQiIGhlaWdodD0iNjY0IiBmaWxsPSJ3aGl0ZSIvPg0KICAgIDwvY2xpcFBhdGg+DQogIDwvZGVmcz4NCjwvc3ZnPg==";
const PLACEHOLDER_IMAGE_LARGE =
  "data:image/svg+xml;charset=utf-8;base64,PHN2ZyB2aWV3Qm94PSIwIDAgNjY0IDY2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCiAgPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzFfMjEpIj4NCiAgICA8cGF0aCBkPSJNNjY0IDBIMFY2NjRINjY0VjBaIiBmaWxsPSIjRjJGMkYyIi8+DQogICAgPHBhdGggZD0iTTMwMC40IDMzNi40TDMxNy45IDM3MS4zSDI4M0wzMDAuNCAzMzYuNFoiIGZpbGw9IiM0MTQxNDEiLz4NCiAgICA8cGF0aCBkPSJNMzM5LjcgMjkyLjhMMzc5IDM3MS4zSDMwMC41TDMzOS43IDI5Mi44WiIgZmlsbD0iIzdGN0Y3RiIvPg0KICAgIDxwYXRoIGQ9Ik0zMDAuNCAzMTQuNEMzMDUuMjA1IDMxNC40IDMwOS4xIDMxMC41MDUgMzA5LjEgMzA1LjdDMzA5LjEgMzAwLjg5NSAzMDUuMjA1IDI5NyAzMDAuNCAyOTdDMjk1LjU5NSAyOTcgMjkxLjcgMzAwLjg5NSAyOTEuNyAzMDUuN0MyOTEuNyAzMTAuNTA1IDI5NS41OTUgMzE0LjQgMzAwLjQgMzE0LjRaIiBmaWxsPSIjRUMxRDI0Ii8+DQogIDwvZz4NCiAgPGRlZnM+DQogICAgPGNsaXBQYXRoIGlkPSJjbGlwMF8xXzIxIj4NCiAgICAgIDxyZWN0IHdpZHRoPSI2NjQiIGhlaWdodD0iNjY0IiBmaWxsPSJ3aGl0ZSIvPg0KICAgIDwvY2xpcFBhdGg+DQogIDwvZGVmcz4NCjwvc3ZnPg==";

export type ImageProps = {
  src: SanityImage | string | undefined;
  alt?: string;
  style?: React.CSSProperties;
  aspectRatio?: number;
  customWidth?: number;
  sizes?: string;
  quality?: number;
  loading?: "lazy" | "eager";
  crop?: "clip" | "crop" | "fill" | "fillmax" | "max" | "scale" | "min";
};

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  style,
  aspectRatio = 16 / 9,
  customWidth = undefined,
  sizes,
  quality,
  loading = "lazy",
  crop = "crop",
}) => {
  const isSanityImage = (image: any): image is SanityImage =>
    typeof image === "object" && image !== null && "asset" in image;

  const isSimpleImage = (image: any): image is string =>
    image !== undefined && image !== null && image !== "";

  const width = customWidth ?? 1088;
  const height = Math.round(width / aspectRatio);

  if (isSanityImage(src) && sizes) {
    const castedImage = src;
    const widths = [225, 320, 450, 600, 768, 900, 1024, 1200, 1440];

    const srcSet = widths
      .map(
        (width) =>
          `${getSanityImageURL(
            castedImage,
            width,
            width / aspectRatio,
            crop,
            quality
          )} ${width}w`
      )
      .join(", ");

    const altText = alt ?? castedImage?.asset.altText;

    return (
      <img
        alt={altText ?? ""}
        srcSet={srcSet}
        sizes={sizes}
        style={style}
        height={height}
        width={width}
        loading={loading}
      />
    );
  }

  if (isSanityImage(src)) {
    const castedImage = src;

    const imgSrc = getSanityImageURL(castedImage, width, height);
    const altText = alt ?? castedImage?.asset.altText;

    return (
      <img
        src={imgSrc}
        alt={altText ?? ""}
        style={style}
        height={height}
        width={width}
        loading={loading}
      />
    );
  }

  if (isSimpleImage(src as any)) {
    return (
      <img
        src={src}
        alt={alt ?? ""}
        style={style}
        height={height}
        width={width}
        loading={loading}
      />
    );
  } else if (src === undefined || src === null || src === "") {
    // Render placeholder image
    const imgRef = useRef(null);
    const [placeholder, setPlaceholder] = useState(PLACEHOLDER_IMAGE_LARGE);

    useEffect(() => {
      const updatePlaceholder = (entries) => {
        for (const entry of entries) {
          const { width } = entry.contentRect;
          setPlaceholder(
            width <= 150 ? PLACEHOLDER_IMAGE_SMALL : PLACEHOLDER_IMAGE_LARGE
          );
        }
      };

      const observer = new ResizeObserver(updatePlaceholder);
      if (imgRef.current) {
        observer.observe(imgRef.current);
      }

      return () => observer.disconnect();
    }, []);

    return (
      <img ref={imgRef} src={placeholder} sizes={sizes} alt="" style={style} />
    );
  }
};

export { Image };
